import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Input, Button } from 'shared-components';
import LoginService, {
	LoginErrorCode,
	isLoginError,
	isLoginRedirect,
	isLoginRequirement,
} from '../../Services/LoginService';
import AccountService from '../../Services/AccountService';
import { useLoginState } from './LoginContext';
import { LoginCard } from './LoginCard';
import Loading from '../../Abstracts/Components/Loading';

const loginErrorCopy: Record<LoginErrorCode | 'default', string> = {
	invalid_credentials: 'That email and password combination was not recognised.',
	invalid_two_factor: 'That code was not recognised. Please try again.',
	invalid_new_password: 'Please choose a stronger password and make sure both entries match.',
	rate_limited: 'Too many attempts. Please wait a moment before trying again.',
	duplicate_email: 'We found multiple accounts with that email. Please contact our office to get this fixed.',
	default: 'Unable to sign in right now. Please try again shortly.',
};

type LoginStep = 'credentials' | 'password-reset' | 'two-factor';

export const LoginForm = () => {
	const { fatalError, setFatalError } = useLoginState();

	// Form Data
	const [email, setEmail] = useState('');
	const [password, setPassword] = useState('');
	const [newPassword, setNewPassword] = useState('');
	const [newPasswordConfirm, setNewPasswordConfirm] = useState('');
	const [authCode, setAuthCode] = useState('');

	// State Control
	const [step, setStep] = useState<LoginStep>('credentials');
	const [requirements, setRequirements] = useState<{ twoFactor: boolean; passwordReset: boolean }>({
		twoFactor: false,
		passwordReset: false,
	});

	const [submitting, setSubmitting] = useState(false);
	const [errorCode, setErrorCode] = useState<LoginErrorCode | 'default' | null>(null);
	const [redirecting, setRedirecting] = useState(false);

	const formatPasswordErrors = (messages: string[]) =>
		`Password must ${messages
			.map((message) => message.replace(/^Password must /, '').replace(/\.$/, ''))
			.join(', ')}.`;

	const attemptLogin = async () => {
		setSubmitting(true);
		setErrorCode(null);
		setFatalError(null);
		try {
			const response = await LoginService.login(
				email.trim(),
				password,
				// Only include new password if we are in/past the reset step and it was required
				requirements.passwordReset ? newPassword : undefined,
				requirements.passwordReset ? newPasswordConfirm : undefined,
				// Only include auth code if we are in/past the 2fa step and it was required
				requirements.twoFactor ? authCode.trim() : undefined,
			);

			if (isLoginError(response)) {
				const code = response.code ?? 'default';
				setErrorCode(code);

				// Automatically navigate to the relevant step to fix the error
				if (code === 'invalid_two_factor') {
					setStep('two-factor');
				} else if (code === 'invalid_new_password') {
					setStep('password-reset');
				} else if (code === 'invalid_credentials') {
					setStep('credentials');
					// Optional: clear sensitive fields
					setPassword('');
					setAuthCode('');
				}
				return;
			}

			if (isLoginRequirement(response)) {
				const nextReqs = {
					twoFactor: response.two_factor_required,
					passwordReset: response.password_reset_required,
				};
				setRequirements(nextReqs);

				// Determine next step based on priority
				if (nextReqs.passwordReset) {
					setStep('password-reset');
				} else if (nextReqs.twoFactor) {
					setStep('two-factor');
				} else {
					// Should not happen if isLoginRequirement is true, but fallback
					setFatalError('Login requirements were empty.');
				}
				return;
			}

			if (isLoginRedirect(response) && response.redirect) {
				setRedirecting(true);
				window.location.assign(response.redirect);
				return;
			}
			setFatalError('We could not determine where to send you after login.');
		} catch (err) {
			console.error(err);
			setFatalError('Something went wrong while signing you in.');
		} finally {
			setSubmitting(false);
		}
	};

	const onSubmit = async (event: React.FormEvent) => {
		event.preventDefault();

		if (step === 'credentials') {
			await attemptLogin();
		} else if (step === 'password-reset') {
			const validation = AccountService.validatePassword(newPassword);
			if (!validation.valid) {
				setErrorCode(null);
				setFatalError(formatPasswordErrors(validation.failures));
				return;
			}
			if (newPassword !== newPasswordConfirm) {
				setErrorCode('invalid_new_password');
				return;
			}
			// If we also need 2FA, move to that step next without submitting yet
			if (requirements.twoFactor) {
				setStep('two-factor');
				setErrorCode(null);
				setFatalError(null);
			} else {
				await attemptLogin();
			}
		} else if (step === 'two-factor') {
			await attemptLogin();
		}
	};

	const errorMessage = errorCode ? (loginErrorCopy[errorCode] ?? loginErrorCopy.default) : null;
	const fatalMessage = fatalError;

	if (redirecting) {
		return (
			<LoginCard>
				<Loading message="Signing you in" />
			</LoginCard>
		);
	}

	let title = 'Sign in';
	if (step === 'password-reset') title = 'Set a new password';
	if (step === 'two-factor') title = 'Enter verification code';

	let buttonLabel = 'Sign in';
	if (submitting) buttonLabel = 'Signing in...';
	else if (step === 'password-reset') buttonLabel = requirements.twoFactor ? 'Next' : 'Save new password';
	else if (step === 'two-factor') buttonLabel = 'Verify and sign in';

	return (
		<LoginCard>
			<h1 className="login-title">{title}</h1>
			<form className="login-form" onSubmit={onSubmit}>
				{step === 'two-factor' && (
					<>
						<p className="login-helper">
							Use your authenticator app to enter the 6-digit code for {email}.
						</p>
						<Input
							id="login-auth-code"
							label="Authentication code"
							type="text"
							value={authCode}
							onChange={(value) => setAuthCode(value)}
							className="login-field"
							inputProps={{
								autoComplete: 'one-time-code',
								required: true,
								inputMode: 'numeric',
								autoFocus: true,
							}}
						/>
					</>
				)}

				{step === 'password-reset' && (
					<>
						<p className="login-helper">
							Your password has expired. Please set a new password to continue.
						</p>
						<Input
							id="login-new-password"
							label="New password"
							type="password"
							value={newPassword}
							onChange={(value) => setNewPassword(value)}
							className="login-field"
							inputProps={{ autoComplete: 'new-password', required: true, autoFocus: true }}
						/>
						<Input
							id="login-new-password-confirm"
							label="Confirm new password"
							type="password"
							value={newPasswordConfirm}
							onChange={(value) => setNewPasswordConfirm(value)}
							className="login-field"
							inputProps={{ autoComplete: 'new-password', required: true }}
						/>
					</>
				)}

				{step === 'credentials' && (
					<>
						<Input
							id="login-email"
							label="Email"
							type="email"
							value={email}
							onChange={(value) => setEmail(value)}
							placeholder="you@example.com"
							className="login-field"
							inputProps={{ autoComplete: 'email', required: true, autoFocus: true }}
						/>
						<Input
							id="login-password"
							label="Password"
							type="password"
							value={password}
							onChange={(value) => setPassword(value)}
							className="login-field"
							inputProps={{ autoComplete: 'current-password', required: true }}
						/>
					</>
				)}

				{errorMessage ? (
					<p className="login-error" role="alert">
						{errorMessage}
					</p>
				) : null}

				{fatalMessage ? (
					<p className="login-error" role="alert">
						{fatalMessage}
					</p>
				) : null}

				<Button
					className="login-button"
					color="primary"
					size="md"
					variant="contained"
					type="submit"
					disabled={submitting}
				>
					{buttonLabel}
				</Button>
			</form>

			{step === 'credentials' && (
				<div className="login-footer">
					<Link to="/reset" className="login-link">
						Forgot password?
					</Link>
				</div>
			)}
		</LoginCard>
	);
};
